import { h } from "preact";
import { useState, useEffect } from "preact/hooks";
import { useParams, useNavigate } from "react-router-dom";
import { Spin, Card, Checkbox, Tag, Typography, Button, Alert, message, Modal, Input, Form } from "antd";
import {
    CheckCircleOutlined,
    StopOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    UndoOutlined,
    LockOutlined,
    CopyOutlined,
    ReloadOutlined,
    EditOutlined
} from "@ant-design/icons";
import { useConsumers } from "./useConsumers.js";
import EditContactModal from "./EditContactModal.jsx";
import ResetPasswordModal from './ResetPasswordModal.jsx';
import PasswordField from "./PasswordField.jsx";

import { nanoid } from 'nanoid';

const { Text } = Typography;
const { confirm } = Modal;

const ConsumerDetailsPage = () => {
    const { getConsumerByConsumerId, changeConsumerStatus, resetConsumerPassword, updateConsumerContact } = useConsumers();
    const { consumer_id } = useParams();
    const navigate = useNavigate();
    const [consumerDetails, setConsumerDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusChangeLoading, setStatusChangeLoading] = useState(false);
    const [passwordResetLoading, setPasswordResetLoading] = useState(false);
    const [contactUpdateLoading, setContactUpdateLoading] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [isTemporary, setIsTemporary] = useState(true);
    const [error, setError] = useState(null);
    const [resetPasswordModal, setResetPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [editContactModal, setEditContactModal] = useState(false);
    const [contactForm] = Form.useForm();

    useEffect(() => {
        if (consumer_id && getConsumerByConsumerId) {
            fetchConsumerDetails();
        }
    }, [consumer_id, getConsumerByConsumerId]);

    const fetchConsumerDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            const details = await getConsumerByConsumerId(consumer_id);
            setConsumerDetails(details);
        } catch (err) {
            console.error("Error fetching consumer details:", err);
            setError("Failed to load consumer details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate("/consumers");
    };

    const handleStatusChange = async () => {
        const currentStatus = consumerDetails?.status?.status?.toLowerCase();
        const newStatus = currentStatus === 'active' ? 'forbidden' : 'active';

        try {
            setStatusChangeLoading(true);
            const response = await changeConsumerStatus(consumer_id, newStatus)
            setConsumerDetails(prev => ({
                ...prev,
                status: {
                    ...prev.status,
                    status: newStatus
                }
            }));

            message.success(`Consumer status changed to ${newStatus.toUpperCase()}`);

        } catch (error) {
            message.error('Failed to change consumer status');
            console.error('Error changing status:', error);
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleArchiveConsumer = () => {
        confirm({
            title: 'Archive Consumer',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Are you sure you want to archive this consumer?</p>
                    <p><strong>Consumer ID:</strong> {consumer_id}</p>
                    <p><strong>Organization:</strong> {consumerDetails?.company || 'Not specified'}</p>
                    <p><strong>Username:</strong> {consumerDetails?.username}</p>
                    <br />
                    <Alert
                        message="Warning"
                        description="The consumer will be moved to archived status and will no longer be able to access the system. You can unarchive it later if needed."
                        type="warning"
                        showIcon
                    />
                </div>
            ),
            okText: 'Archive Consumer',
            okType: 'danger',
            cancelText: 'Cancel',
            width: 520,
            onOk: async () => {
                try {
                    setArchiveLoading(true);

                    await changeConsumerStatus(consumer_id, 'archived');

                    setConsumerDetails(prev => ({
                        ...prev,
                        status: {
                            ...prev.status,
                            status: 'archived'
                        }
                    }));

                    message.success('Consumer has been archived successfully');

                } catch (error) {
                    message.error('Failed to archive consumer');
                    console.error('Error archiving consumer:', error);
                } finally {
                    setArchiveLoading(false);
                }
            },
        });
    };

    const handleUnarchiveConsumer = () => {
        confirm({
            title: 'Unarchive Consumer',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Are you sure you want to unarchive this consumer?</p>
                    <p><strong>Consumer ID:</strong> {consumer_id}</p>
                    <p><strong>Organization:</strong> {consumerDetails?.company || 'Not specified'}</p>
                    <p><strong>Username:</strong> {consumerDetails?.username}</p>
                    <br />
                    <Alert
                        message="Information"
                        description="The consumer will be restored to active status and will be able to access the system again."
                        type="info"
                        showIcon
                    />
                </div>
            ),
            okText: 'Unarchive Consumer',
            okType: 'primary',
            cancelText: 'Cancel',
            width: 520,
            onOk: async () => {
                try {
                    setArchiveLoading(true);

                    await changeConsumerStatus(consumer_id, 'active');

                    // Update local state to reflect the change
                    setConsumerDetails(prev => ({
                        ...prev,
                        status: {
                            ...prev.status,
                            status: 'active'
                        }
                    }));

                    message.success('Consumer has been unarchived and is now active');

                } catch (error) {
                    message.error('Failed to unarchive consumer');
                    console.error('Error unarchiving consumer:', error);
                } finally {
                    setArchiveLoading(false);
                }
            },
        });
    };

    const handlePasswordReset = async () => {
        if (!newPassword.trim()) {
            message.error('Please enter a new password');
            return;
        }

        if (newPassword !== confirmPassword) {
            message.error('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            message.error('Password must be at least 6 characters long');
            return;
        }

        try {
            setPasswordResetLoading(true);

            const updatePasswordData = {
                username: consumerDetails.username,
                password: newPassword,
                temporary: isTemporary,
            }

            await resetConsumerPassword(updatePasswordData);

            setConsumerDetails(prev => ({
                ...prev,
                password: isTemporary ? newPassword : null
            }));

            message.success('Password reset successfully');
            setResetPasswordModal(false);
            setNewPassword('');
            setConfirmPassword('');

        } catch (error) {
            message.error('Failed to reset password');
            console.error('Error resetting password:', error);
        } finally {
            setPasswordResetLoading(false);
        }
    };

    const generateRandomPassword = () => {
        const password = nanoid(12);
        setNewPassword(password);
        setConfirmPassword(password);
    };

    const openResetPasswordModal = () => {
        setResetPasswordModal(true);
        setNewPassword('');
        setConfirmPassword('');
    };

    const closeResetPasswordModal = () => {
        setResetPasswordModal(false);
        setNewPassword('');
        setConfirmPassword('');
        setIsTemporary(true);
    };

    const handleContactUpdate = async (values) => {
        try {
            setContactUpdateLoading(true);

            await updateConsumerContact(consumer_id, values);

            setConsumerDetails(prev => ({
                ...prev,
                ...values
            }));

            message.success('Contact information updated successfully');
            setEditContactModal(false);
            contactForm.resetFields();

        } catch (error) {
            message.error('Failed to update contact information');
            console.error('Error updating contact:', error);
        } finally {
            setContactUpdateLoading(false);
        }
    };

    const getFormValuesFromConsumer = (consumer) => {
        const fields = [
            'company', 'country', 'city', 'address',
            'contact_person_full_name', 'contact_person_email', 'contact_person_phone_number'
        ];

        return fields.reduce((values, field) => ({
            ...values,
            [field]: consumer?.[field] || ''
        }), {});
    };

    const openEditContactModal = () => {
        setEditContactModal(true);
        contactForm.setFieldsValue(getFormValuesFromConsumer(consumerDetails));
    };

    const closeEditContactModal = () => {
        setEditContactModal(false);
        contactForm.resetFields();
    };

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'green';
            case 'forbidden':
                return 'red';
            case 'archived':
                return 'gray';
            default:
                return 'default';
        }
    };

    const isArchived = () => {
        return consumerDetails?.status?.status?.toLowerCase() === 'archived';
    };

    const isActive = () => {
        return consumerDetails?.status?.status?.toLowerCase() === 'active';
    };

    const isForbidden = () => {
        return consumerDetails?.status?.status?.toLowerCase() === 'forbidden';
    };

    const formatLocation = (details) => {
        if (!details) return "Not specified";

        const parts = [details.country, details.city, details.address]
            .filter(part => part && part.trim() !== "");

        return parts.length > 0 ? parts.join(", ") : "Not specified";
    };

    const renderContactInfo = (details) => (
        <Card title="CONTACTS"
              style={{ marginBottom: 24 }}
              extra={
                  <Button
                      type="default"
                      icon={<EditOutlined />}
                      onClick={openEditContactModal}
                      size="small"
                      style={{ fontSize: "12px" }}
                  >
                      Edit Contact
                  </Button>
              }
        >
            <div style={{ display: "grid", gap: "20px" }}>
                <div>
                    <Text strong style={{ display: "block", marginBottom: 8, fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                        Organization
                    </Text>
                    <div style={{
                        backgroundColor: "#fafafa",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        fontSize: "16px"
                    }}>
                        {details.company || "Not specified"}
                    </div>
                </div>

                <div>
                    <Text strong style={{ display: "block", marginBottom: 8, fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                        Location
                    </Text>
                    <div style={{
                        backgroundColor: "#fafafa",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        fontSize: "16px"
                    }}>
                        {formatLocation(details)}
                    </div>
                </div>

                <div>
                    <Text strong style={{ display: "block", marginBottom: 8, fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                        Contact Person Full Name
                    </Text>
                    <div style={{
                        backgroundColor: "#fafafa",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        fontSize: "16px"
                    }}>
                        {details.contact_person_full_name || "Not specified"}
                    </div>
                </div>

                <div>
                    <Text strong style={{ display: "block", marginBottom: 8, fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                        Contact Person Email
                    </Text>
                    <div style={{
                        backgroundColor: "#fafafa",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        fontSize: "16px"
                    }}>
                        {details.contact_person_email || "Not specified"}
                    </div>
                </div>

                {details.contact_person_phone_number && (
                    <div>
                        <Text strong style={{ display: "block", marginBottom: 8, fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                            Contact Person Phone Number
                        </Text>
                        <div style={{
                            backgroundColor: "#fafafa",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            border: "1px solid #f0f0f0",
                            fontSize: "16px"
                        }}>
                            {details.contact_person_phone_number}
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );

    const renderCredentials = (details) => (
        <Card
            title="CREDENTIALS"
            style={{ marginBottom: 24 }}
            extra={
                <Button
                    type="default"
                    icon={<LockOutlined />}
                    onClick={openResetPasswordModal}
                    size="small"
                    style={{ fontSize: "12px" }}
                >
                    Reset Password
                </Button>
            }
        >
            <div style={{display: "grid", gap: "20px"}}>
                <div>
                    <Text strong style={{
                        display: "block",
                        marginBottom: 8,
                        fontSize: "14px",
                        color: "#666",
                        textTransform: "uppercase"
                    }}>
                        Username
                    </Text>
                    <div style={{
                        backgroundColor: "#fafafa",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        fontSize: "16px",
                        fontFamily: "monospace"
                    }}>
                        {details.username}
                    </div>
                </div>
                <PasswordField password={details.password}/>
            </div>
        </Card>
    );

    const renderApiKeys = (details) => (
        <Card title="API KEYS">
            {details.access_keys && details.access_keys.length > 0 ? (
                <div style={{display: "grid", gap: "16px"}}>
                    {details.access_keys.map((key, index) => (
                        <div key={index} style={{
                            border: "1px solid #f0f0f0",
                            padding: "20px",
                            borderRadius: "8px",
                            backgroundColor: "#fafafa"
                        }}>
                            <div style={{display: "grid", gap: "12px"}}>
                                <div>
                                    <Text strong style={{fontSize: "14px", color: "#666", textTransform: "uppercase"}}>
                                        API Key:
                                    </Text>
                                    <div style={{
                                        backgroundColor: "#ffffff",
                                        padding: "8px 12px",
                                        borderRadius: "4px",
                                        marginTop: "4px",
                                        fontSize: "14px",
                                        fontFamily: "monospace",
                                        border: "1px solid #e8e8e8",
                                        wordBreak: "break-all"
                                    }}>
                                        {key.api_key}
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                                    <div>
                                        <Text strong style={{ fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                                            Status:
                                        </Text>
                                        <Tag color={key.status === 'active' ? 'green' : 'red'} style={{ marginLeft: "8px" }}>
                                            {key.status?.toUpperCase()}
                                        </Tag>
                                    </div>
                                    <div>
                                        <Text strong style={{ fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                                            Target:
                                        </Text>
                                        <Tag color="blue" style={{ marginLeft: "8px" }}>
                                            {key.target?.toUpperCase()}
                                        </Tag>
                                    </div>
                                    <div>
                                        <Text strong style={{ fontSize: "14px", color: "#666", textTransform: "uppercase" }}>
                                            Created:
                                        </Text>
                                        <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                                            {new Date(key.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{
                    backgroundColor: "#f5f5f5",
                    padding: "24px",
                    borderRadius: "8px",
                    textAlign: "center",
                    color: "#666",
                    fontSize: "16px"
                }}>
                    No API keys found
                </div>
            )}
        </Card>
    );

    return (
        <div style={{
            minHeight: "100vh",
            backgroundColor: "#f5f5f5",
            padding: "24px"
        }}>
            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                {(!loading && !error && consumerDetails) && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "24px",
                        backgroundColor: "white",
                        padding: "16px 24px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                    }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ fontSize: "14px", color: "#666", fontWeight: 500 }}>Status:</span>
                                <Tag
                                    color={getStatusColor(consumerDetails.status?.status)}
                                    style={{ fontSize: "14px", padding: "4px 12px", fontWeight: 500 }}
                                >
                                    {consumerDetails.status?.status?.toUpperCase() || "UNKNOWN"}
                                </Tag>
                            </div>
                            <div style={{ display: "flex", gap: "12px" }}>
                                {!isArchived() && (
                                    <Button
                                        type={isActive() ? "danger" : "primary"}
                                        icon={isActive() ? <StopOutlined /> : <CheckCircleOutlined />}
                                        onClick={handleStatusChange}
                                        size="large"
                                        style={{
                                            backgroundColor: isActive() ? "#ff4d4f" : "#52c41a",
                                            borderColor: isActive() ? "#ff4d4f" : "#52c41a",
                                            fontWeight: 500,
                                        }}
                                        disabled={statusChangeLoading}
                                        loading={statusChangeLoading}
                                    >
                                        {isActive() ? "Set Forbidden" : "Set Active"}
                                    </Button>
                                )}

                                {isArchived() ? (
                                    <Button
                                        type="primary"
                                        icon={<UndoOutlined />}
                                        onClick={handleUnarchiveConsumer}
                                        size="large"
                                        style={{
                                            fontWeight: 500,
                                            backgroundColor: "#52c41a",
                                            borderColor: "#52c41a"
                                        }}
                                        disabled={archiveLoading}
                                        loading={archiveLoading}
                                    >
                                        Unarchive Consumer
                                    </Button>
                                ) : (
                                    <Button
                                        type="default"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleArchiveConsumer}
                                        size="large"
                                        style={{
                                            fontWeight: 500,
                                            borderColor: "#ff7875",
                                            color: "#ff7875"
                                        }}
                                        disabled={archiveLoading}
                                        loading={archiveLoading}
                                    >
                                        Archive Consumer
                                    </Button>
                                )}
                            </div>
                    </div>
                )}

                {loading && (
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "60px",
                        backgroundColor: "white",
                        borderRadius: "8px"
                    }}>
                        <Spin size="large" />
                    </div>
                )}

                {error && (
                    <Alert
                        message="Error"
                        description={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: "24px" }}
                    />
                )}

                {!loading && !error && consumerDetails && (
                    <div>
                        {isArchived() && (
                            <Alert
                                message="Archived Consumer"
                                description="This consumer is currently archived and cannot access the system. Use the 'Unarchive Consumer' button to restore access."
                                type="warning"
                                showIcon
                                style={{ marginBottom: "24px" }}
                            />
                        )}

                        {renderContactInfo(consumerDetails)}
                        {renderCredentials(consumerDetails)}
                        {renderApiKeys(consumerDetails)}
                    </div>
                )}

                <EditContactModal
                    open={editContactModal}
                    onCancel={closeEditContactModal}
                    onSubmit={handleContactUpdate}
                    loading={contactUpdateLoading}
                    consumerUsername={consumerDetails?.username}
                    form={contactForm}
                    initialValues={{
                        company: consumerDetails?.company || '',
                        country: consumerDetails?.country || '',
                        city: consumerDetails?.city || '',
                        address: consumerDetails?.address || '',
                        contact_person_full_name: consumerDetails?.contact_person_full_name || '',
                        contact_person_email: consumerDetails?.contact_person_email || '',
                        contact_person_phone_number: consumerDetails?.contact_person_phone_number || ''
                    }}
                />

                <ResetPasswordModal
                    open={resetPasswordModal}
                    onCancel={closeResetPasswordModal}
                    onOk={handlePasswordReset}
                    loading={passwordResetLoading}
                    consumerUsername={consumerDetails?.username}
                    newPassword={newPassword}
                    setNewPassword={setNewPassword}
                    confirmPassword={confirmPassword}
                    setConfirmPassword={setConfirmPassword}
                    isTemporary={isTemporary}
                    setIsTemporary={setIsTemporary}
                />
            </div>
        </div>
    );
};

export default ConsumerDetailsPage;